/**
 * PATH: packages/ui/src/kanban/preferences/KanbanSettingsPreferenceContext.ts
 * FILE_ID: UI-KANBAN-PREFERENCES-CONTEXT-001
 * PURPOSE: Define the board-level preference graph shared by Kanban consumers.
 * ROLE IN ARCHITECTURE: React context contract between provider and domain hooks.
 * MAIN EXPORTS: KanbanSettingsPreferenceContext, useKanbanSettingsPreferences.
 * NON-RESPONSIBILITIES: Does not hydrate, persist, coerce, or render settings.
 * NOTES FOR FUTURE AI: Add scopes here only when they are shared board concerns.
 */

import { createContext, useContext } from 'react'

import type {
  ViewPreferenceConflict,
  ViewPreferenceSyncState,
} from '@quotech/api/view-preferences'

import type { KanbanCardFacePrefs } from '../components/board-view/cardFacePrefs'
import type { CardTabLayout } from '../components/card-detail/drawer-sections/cardTabLayout'
import type { DrawerSectionPrefs } from '../components/card-detail/drawer-sections/drawerSectionPrefs'

export interface KanbanPreferenceSlice<T extends object> {
  value: T
  setValue: (next: T | ((previous: T) => T)) => void
  hydrated: boolean
  syncState: ViewPreferenceSyncState
  conflict: ViewPreferenceConflict<T> | null
  error: string | null
}

export interface KanbanSettingsPreferenceContextValue {
  boardId: string
  boardKind?: string | null
  cardFace: KanbanPreferenceSlice<KanbanCardFacePrefs>
  cardTabLayout: KanbanPreferenceSlice<CardTabLayout>
  drawerSections: KanbanPreferenceSlice<DrawerSectionPrefs>
}

export const KanbanSettingsPreferenceContext =
  createContext<KanbanSettingsPreferenceContextValue | null>(null)

/** Return the board preference runtime or null for isolated card/story renders. */
export function useOptionalKanbanSettingsPreferences():
  | KanbanSettingsPreferenceContextValue
  | null {
  return useContext(KanbanSettingsPreferenceContext)
}

/** Require a mounted board runtime for provider-owned settings surfaces. */
export function useKanbanSettingsPreferences(): KanbanSettingsPreferenceContextValue {
  const context = useOptionalKanbanSettingsPreferences()
  if (!context) {
    throw new Error('useKanbanSettingsPreferences requires KanbanSettingsPreferenceProvider')
  }
  return context
}
